<template>
  <JoszakiAutocomplete
    ref="professionAutocomplete"
    :value="value"
    v-bind="$attrs"
    icon="briefcase"
    :items="filteredProfessions"
    exclude-selected
    :group-id-field="searchValue ? null : 'professionGroup.id'"
    :group-value-field="searchValue ? null : 'professionGroup.name'"
    :formatter="formatter"
    :label="showLabel ? label ?? $t('common.profession') : null"
    :keep-first="false"
    :placeholder="value?.name ?? placeholder ?? ''"
    :loading="professionsLoading"
    v-on="$listeners"
    @input="$emit('input', $event)"
    @search="onSearch"
    @focus="onFocus"
  >
    <template #empty>
      <slot name="empty">
        <span>
          {{ $t("professionAutocomplete.empty") }}
        </span>
      </slot>
    </template>
  </JoszakiAutocomplete>
</template>

<script>
import { useContext, ref } from "@nuxtjs/composition-api";
import { mapState } from "pinia";
import { useLazyStore } from "~/stores/lazy";
import { useZoneStore } from "~/stores/zone";

export default {
  props: {
    value: {
      type: [Object, Array],
      default: () => null,
    },
    typingTrackerEvent: {
      type: String,
      default: null,
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: Function,
      default: (item) => item.name,
    },
    itemsMapper: {
      type: Function,
      default: (item) => item,
    },
    label: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: null,
    },
    onlyWithProfessionTasks: {
      type: Boolean,
      default: false,
    },
    onlyNonMiniMarketProfessions: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { $trackers } = useContext();
    const searchValue = ref("");
    const onSearch = (search) => {
      searchValue.value = search;
      if (!search || search.length < 3 || !props.typingTrackerEvent) {
        return;
      }

      $trackers.trackEvent({
        title: props.typingTrackerEvent,
        data: { value: search },
      });
      emit("search", search);
    };
    return {
      lazyStore: useLazyStore(),
      searchValue,
      onSearch,
    };
  },
  computed: {
    ...mapState(useLazyStore, {
      professions: "professions",
      professionsLoading: "professionsLoading",
    }),
    ...mapState(useZoneStore, {
      professionIdsWithMiniMarkets: "professionIdsWithMiniMarkets",
    }),
    normalizedSearchValue() {
      return this.$helpers.normalizeInput(this.searchValue);
    },
    filteredProfessions() {
      let professions = this.professions;

      if (this.onlyWithProfessionTasks) {
        professions = professions.filter((p) => p.professionTaskCount > 0);
      }

      if (this.onlyNonMiniMarketProfessions) {
        professions = professions.filter(
          (p) => !this.professionIdsWithMiniMarkets.includes(p.id)
        );
      }

      if (this.searchValue) {
        professions = professions.filter((p) =>
          this.$helpers
            .normalizeInput(p.name)
            .includes(this.normalizedSearchValue)
        );
      }

      return professions.map(this.itemsMapper);
    },
  },
  methods: {
    onFocus() {
      this.lazyStore.loadProfessions();
      this.$emit("focus");
    },
  },
};
</script>
